import type { BadgeFields } from '@tribeplatform/gql-client/graphql'
import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  Badge,
  MutationCreateBadgeArgs,
  Network,
} from '@tribeplatform/gql-client/types'
import { BadgeType } from '@tribeplatform/gql-client/types'

import {
  useMutation,
  type UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getAuthTokenKey } from '../../utils/keys/authToken.keys.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'

export const useCreateBadge = (options?: {
  fields?: BadgeFields
  useMutationOptions?: UseMutationOptions<
    Badge,
    ClientError,
    MutationCreateBadgeArgs
  >
}) => {
  const { useMutationOptions, fields = 'basic' } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const networkKey = getNetworkKey()
  const authTokenKey = getAuthTokenKey()

  return useMutation<Badge, ClientError, MutationCreateBadgeArgs>(
    input => client.badge.create(input, fields),
    {
      onMutate: async variables => {
        await queryClient.cancelQueries(networkKey)
        await queryClient.cancelQueries(authTokenKey)

        queryClient.setQueriesData<Network>(networkKey, old => ({
          ...old,
          badges: [
            ...(old?.badges ?? []),
            {
              ...variables?.input,
              id: '',
              type: BadgeType.Manual,
              networkId: '',
              name: variables?.input?.name ?? '',
            },
          ],
        }))
      },
      onSettled: () => {
        queryClient.invalidateQueries(networkKey)
        queryClient.invalidateQueries(authTokenKey)
      },
      ...useMutationOptions,
    },
  )
}
